<script>
  // Team-Umfrage im Schritt "Configure Framework": je Team ein Link (Stufe 1 Aufgaben sammeln,
  // Stufe 2 gemeinsam auswaehlen). Das Ergebnis fuellt den Framework Assistant vor, der L1-L4 vorschlaegt.
  // Das Einrichtungsmenue folgt dem Potential Scout (Start, Fristen, Erinnerungen, Kontakt, Signatur) ohne
  // Tasks und KPIs; versendet wird ueber dessen Mailversand.
  import {
    createEventDispatcher,
    getContext,
    onDestroy,
    onMount,
  } from "svelte";
  import { useNavigate } from "svelte-navigator";
  import Button from "lib/components/Button.svelte";
  import Modal from "@components/Modal.svelte";
  import { TextInput } from "paxray-components-svelte";
  import TeamSurveyMapping from "./TeamSurveyMapping.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { ROUTES } from "@utils/routes.js";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";

  export let idproject;
  // Im Framework Assistant (als Fenster): der Assistent laedt den Entwurf selbst neu, statt auf seine Seite zu springen
  export let inAssistant = false;
  export let startWith = null; // aus dem Assistenten: { mode } neue Umfrage einrichten oder { transfer: id } uebernehmen

  // Gleicher Schluessel wie in FrameworkAssistantChat.svelte (draftStorageKey)
  const ASSISTANT_DRAFT_KEY = "paxray.framework-assistant.draft";
  const STAGES = {
    collect: "Collecting tasks",
    select: "Choosing together",
    done: "Done",
  };
  // Collection scope in Abschnitt 2 - gleicher Schluessel wie collectionStorageKey in ProcessFramework.svelte
  const scopeKey = (id) => `paxray.project-collection-preview.v1.${id}`;
  const dispatch = createEventDispatcher();
  const navigate = useNavigate();

  let teams = [];
  export let surveys = []; // gebunden von ProcessPresets fuer die Schrittleiste (FrameworkSteps)
  export let departments = []; // Abteilungen des Projekts - der gefuehrte Assistent startet mit ihnen
  let loading = true;
  let error = "";
  let editing = null; // null | "new" | Umfrage-Id
  let settings = blankSettings();
  let participantErrors = [];
  let mailPreview = {};
  let busy = "";
  let openResults = {};
  let results = {};
  let copied = "";
  let pollTimer = null;
  let notice = {};
  let lastTestEmail = "";
  let testMailSurvey = null;
  let testEmail = "";
  let testEmailInput = null;
  let testEmailInvalid = false;
  let testMailError = "";
  let sendingTestMail = false;
  let open = true; // Abschnitt 1 in Configure Framework, einklappbar wie Abschnitt 2
  export let framework = { l2: 0, l3: 0, l4: 0, l3_empty: 0 }; // Stand des Projekt-Frameworks - entscheidet, was Transfer macht
  let mapping = null; // Umfrage, deren Zuordnen-Ansicht offen ist
  let deleting = null; // Umfrage im Loeschdialog - bestaetigt wird wie bei Projekten mit dem Namen
  let deleteConfirmName = "";
  $: deleteNameMismatch = deleteConfirmName.trim() !== (deleting?.team || "");

  $: freeTeams = teams.filter(
    (team) => !surveys.some((survey) => survey.team_id === team.id),
  );
  $: assistantEnabled = $uiFeatureStore?.platformFeatures?.frameworkAssistant;
  // Transfer vorerst nur fuer Admins - fuer normale Nutzer wird er noch nachgezogen
  const role = getContext("role");
  $: isAdmin = $role?.role === "admin";

  function localDate(value) {
    const date = new Date(value);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  function daysFromNow(days) {
    return localDate(Date.now() + days * 86400000);
  }

  function addDays(date, days) {
    return localDate(new Date(`${date}T12:00:00`).getTime() + days * 86400000);
  }

  // Fristen gelten bis zum Ende des gewaehlten Tages (Ortszeit)
  function endOfDay(date) {
    return new Date(`${date}T23:59:00`).toISOString();
  }

  function blankSettings() {
    return {
      team_id: "",
      team_name: "",
      mode: "collect",
      participants: "",
      start: "now",
      start_at: "",
      collect_until: daysFromNow(2),
      select_until: daysFromNow(4),
      reminder: true,
      reminder_days: 1,
      reminder2: false,
      reminder2_days: 2,
      language: "de",
      contact_person: "",
      contact_person_email: "",
      email_signature: "",
      period_from: daysFromNow(2 + 7),
      period_until: daysFromNow(2 + 28), // Erhebung 1 bis 4 Wochen nach Umfrage 1
    };
  }

  // Eine Person je Zeile: "Name <mail>" oder nur die Adresse
  function parseParticipants(text) {
    participantErrors = [];
    return text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .flatMap((line, index) => {
        const email = line.match(/[^\s<>,;]+@[^\s<>,;]+\.[^\s<>,;]+/)?.[0];
        if (!email) {
          participantErrors = [
            ...participantErrors,
            `Line ${index + 1}: no e-mail address`,
          ];
          return [];
        }
        return [
          {
            email,
            name: line
              .replace(email, "")
              .replace(/[<>,;]/g, " ")
              .trim(),
          },
        ];
      });
  }

  function formatParticipants(people) {
    return people
      .map((person) =>
        person.name ? `${person.name} <${person.email}>` : person.email,
      )
      .join("\n");
  }

  function pickTeam() {
    const team = teams.find((item) => item.id === settings.team_id);
    settings = {
      ...settings,
      participants: formatParticipants(team?.members || []),
    };
  }

  // Aus der Schrittleiste: neue Umfrage mit vorgewaehltem Inhalt (beide Umfragen oder nur auswaehlen)

  // team: { id } vorhandenes oder { name } neues Team, im Assistenten gewaehlt
  export function openNew(mode, team = null) {
    open = true;
    settings = { ...blankSettings(), mode };
    // direkt pruefen - freeTeams ist reaktiv und direkt nach dem Laden noch nicht aktualisiert
    if (
      team?.id &&
      teams.some((item) => item.id === team.id) &&
      !surveys.some((survey) => survey.team_id === team.id)
    ) {
      settings.team_id = team.id;
      pickTeam();
    } else if (team?.name && !team.id) {
      settings = { ...settings, team_id: "new", team_name: team.name };
    }
    editing = "new";
  }

  async function openSettings(survey) {
    if (!survey) {
      settings = blankSettings();
      editing = "new";
      return;
    }
    try {
      const detail = await request(`team-surveys/${survey.id}`);
      const started =
        detail.start_at && new Date(detail.start_at) <= new Date();
      settings = {
        ...blankSettings(),
        participants: formatParticipants(detail.participants),
        mode: detail.mode,
        start: started ? "started" : detail.start_at ? "later" : "off",
        start_at:
          detail.start_at && !started
            ? localDate(detail.start_at) +
              "T" +
              new Date(detail.start_at).toTimeString().slice(0, 5)
            : "",
        collect_until: localDate(detail.collect_until),
        select_until: localDate(detail.select_until),
        reminder: Boolean(detail.reminder_days),
        reminder_days: detail.reminder_days || 1,
        reminder2: Boolean(detail.reminder2_days),
        reminder2_days: detail.reminder2_days || 2,
        language: detail.language,
        contact_person: detail.contact_person,
        contact_person_email: detail.contact_person_email,
        email_signature: detail.email_signature,
        period_from: detail.period_from ? localDate(detail.period_from) : "",
        period_until: detail.period_until ? localDate(detail.period_until) : "",
      };
      editing = survey.id;
    } catch (loadError) {
      error = loadError.message;
    }
  }

  function settingsPayload() {
    const payload = {
      participants: parseParticipants(settings.participants),
      // Nur eine Umfrage: die Frist der anderen Stufe ist ohne Bedeutung und darf die Pruefung nicht stoeren
      collect_until:
        settings.mode === "select"
          ? undefined
          : endOfDay(settings.collect_until),
      select_until: endOfDay(
        settings.mode === "collect"
          ? addDays(settings.collect_until, 2)
          : settings.select_until,
      ),
      reminder_days: settings.reminder ? settings.reminder_days : null,
      reminder2_days: settings.reminder2 ? settings.reminder2_days : null,
      language: settings.language,
      contact_person: settings.contact_person,
      contact_person_email: settings.contact_person_email,
      email_signature: settings.email_signature,
      // Aufnahmezeitraum: Umfrage 1 fragt je Aufgabe, ob sie darin vorkommt - leer heisst keine Frage
      period_from: settings.period_from
        ? new Date(`${settings.period_from}T00:00:00`).toISOString()
        : null,
      period_until: settings.period_until
        ? endOfDay(settings.period_until)
        : null,
    };
    if (settings.start !== "started") {
      payload.start_at =
        settings.start === "now"
          ? "now"
          : settings.start === "later" && settings.start_at
            ? new Date(settings.start_at).toISOString()
            : null;
    }
    return payload;
  }

  async function request(path, options = {}) {
    const response = await fetchWithSession(`/api/damprojects/${path}`, {
      ...options,
      headers: options.body
        ? { "Content-Type": "application/json" }
        : undefined,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.message || "The request failed.");
    return result;
  }

  async function load() {
    try {
      const result = await request(`team-surveys?idproject=${idproject}`);
      teams = result.teams;
      surveys = result.surveys;
      framework = result.framework || framework;
      departments = result.departments || [];
      if (loading) dispatch("loaded"); // einmal nach dem ersten Laden: Seite entscheidet ueber den gefuehrten Einstieg
      if (startWith) {
        const start = startWith;
        startWith = null;
        if (start.mode) openNew(start.mode, start.team);
        const survey = surveys.find((item) => item.id === start.transfer);
        if (survey && isAdmin) startTransfer(survey);
      }
      error = "";
      // Das Zusammenfassen laeuft im Backend im Hintergrund (bis ca. 1 Minute) - bis dahin nachfragen
      clearTimeout(pollTimer);
      if (surveys.some((survey) => survey.preparing))
        pollTimer = setTimeout(load, 5000);
    } catch (loadError) {
      error = loadError.message;
    } finally {
      loading = false;
    }
  }

  function surveyLink(survey) {
    return `${window.location.origin}${ROUTES.teamSurvey}?id=${survey.id}`;
  }

  async function copyLink(survey) {
    try {
      await navigator.clipboard.writeText(surveyLink(survey));
      copied = survey.id;
      setTimeout(() => (copied = ""), 2000);
    } catch {
      window.prompt("Copy the survey link:", surveyLink(survey));
    }
  }

  async function saveSettings() {
    const payload = settingsPayload();
    if (participantErrors.length) return;
    busy = "settings";
    try {
      if (editing === "new") {
        const body = {
          ...payload,
          idproject,
          mode: settings.mode,
          team_id: settings.team_id === "new" ? "" : settings.team_id,
          team_name: settings.team_name,
        };
        const survey = await request("team-surveys", {
          method: "POST",
          body: JSON.stringify(body),
        });
        if (!payload.participants.length) copyLink(survey);
      } else {
        await request(`team-surveys/${editing}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      }
      editing = null;
      error = "";
      await load();
    } catch (saveError) {
      error = saveError.message;
    } finally {
      busy = "";
    }
  }

  async function remindNow(survey) {
    if (busy || confirmDialog) return;
    if (
      !(await askConfirm(
        "Send reminders?",
        `Send reminder e-mails now to everyone in "${survey.team}" who has not answered the current survey?`,
        "Send reminders",
        true,
      ))
    )
      return;
    busy = survey.id;
    try {
      const result = await request(`team-surveys/${survey.id}/remind`, {
        method: "POST",
      });
      notice = {
        ...notice,
        [survey.id]: result.sent
          ? `Reminder sent to ${result.sent} people${result.failed ? `, ${result.failed} failed` : ""}.`
          : "Nobody to remind. Everyone invited has answered.",
      };
      await load();
    } catch (remindError) {
      error = remindError.message;
    } finally {
      busy = "";
    }
  }

  async function showMailPreview(survey) {
    if (mailPreview[survey.id]) {
      mailPreview = { ...mailPreview, [survey.id]: null };
      return;
    }
    try {
      const result = await request(`team-surveys/${survey.id}/test-mail`, {
        method: "POST",
        body: JSON.stringify({ email: "preview@example.com", dry_run: true }),
      });
      mailPreview = { ...mailPreview, [survey.id]: result.preview };
    } catch (previewError) {
      error = previewError.message;
    }
  }

  function openTestMail(survey) {
    testEmail = lastTestEmail;
    testEmailInvalid = false;
    testMailError = "";
    testMailSurvey = survey;
  }

  function validateTestEmail(input) {
    testEmailInvalid =
      !input?.validity.valid ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim());
    testMailError = "";
  }

  async function sendTestMail() {
    if (!testMailSurvey || sendingTestMail) return;
    const email = testEmail.trim();
    validateTestEmail(testEmailInput);
    if (testEmailInvalid) return;
    const survey = testMailSurvey;
    lastTestEmail = email;
    sendingTestMail = true;
    testMailError = "";
    try {
      const result = await request(`team-surveys/${survey.id}/test-mail`, {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      notice = {
        ...notice,
        [survey.id]:
          result.mail === "sent"
            ? `Test mail sent to ${email}.`
            : `Test mail failed: ${result.message}`,
      };
      testMailSurvey = null;
    } catch (testError) {
      testMailError = testError.message;
    } finally {
      sendingTestMail = false;
    }
  }

  function mailLine(survey) {
    const { invites, mails } = survey;
    if (!invites.total)
      return "No personal invitations. Copy the link in Settings and share it yourself";
    const people = `${invites.total} ${invites.total === 1 ? "person" : "people"}`;
    if (!survey.start_at) return `${people} · e-mails off`;
    if (new Date(survey.start_at) > new Date())
      return `${people} · invitations go out ${formatDate(survey.start_at)}`;
    const stage = survey.stage === "select" ? "select" : "collect";
    const failed = Object.values(mails.failed).reduce(
      (sum, count) => sum + count,
      0,
    );
    return [
      `${mails.sent[`invite_${stage}`] || 0} of ${invites.total} invited`,
      failed ? `${failed} mails failed` : "",
      mails.next_reminder
        ? `next reminder ${formatDate(mails.next_reminder)}`
        : "",
    ]
      .filter(Boolean)
      .join(" · ");
  }

  // Ein Knopf fuer den naechsten Schritt: eine Umfrage starten (Einladungen gehen raus) oder schliessen.
  // Umfrage 2 starten heisst bei "Survey 1 + 2": Umfrage 1 schliessen und die Antworten zusammenfassen.
  function nextStep(survey) {
    if (survey.stage === "done") return null;
    const number = survey.stage === "collect" ? 1 : 2;
    // Noch nichts verschickt und noch keine Antwort: zuerst einladen. Kamen schon Antworten ueber den Link, geht es
    // normal weiter - verschicken bleibt unter Settings moeglich (Test 04.10.: sonst liess sich Umfrage 1 nicht schliessen)
    const answered =
      survey.answers[survey.stage === "collect" ? "collect" : "select"];
    const waiting =
      survey.invites.total &&
      !answered &&
      (!survey.start_at || new Date(survey.start_at) > new Date());
    // auch wenn ueber den Link schon geantwortet wird: der Schritt verschickt die Einladungen (Test 04.10.)
    if (waiting)
      return {
        label: "Send invitations",
        title: `Sends the invitations for ${survey.mode === "collect" ? "the survey" : `survey ${number}`} to ${survey.invites.total} people now`,
        run: () => startNow(survey),
      };
    if (survey.stage === "select")
      return {
        label: "Close survey 2",
        title: "Ends choosing; Transfer then uses the result",
        run: () => update(survey, { stage: "done" }),
      };
    const nobody = !survey.answers.collect;
    if (survey.mode === "collect") {
      return {
        label: "Close survey",
        title: nobody
          ? "Nobody has answered yet"
          : "Ends collecting and merges the answers",
        run: () => startSelection(survey),
        disabled: nobody,
      };
    }
    return {
      label: "Start survey 2",
      title: nobody
        ? "Nobody has answered yet"
        : "Closes survey 1, merges the answers and opens survey 2",
      run: () => startSelection(survey),
      disabled: nobody,
    };
  }

  async function startNow(survey) {
    const people = `${survey.invites.total} ${survey.invites.total === 1 ? "person" : "people"}`;
    if (
      !(await askConfirm(
        "Send invitations?",
        `Send the invitations to ${people} now?`,
        "Send invitations",
      ))
    )
      return;
    update(survey, { start_at: "now" });
  }

  // Transfer je nach Stand: nur Umfrage 2 -> Collection scope; Prozesse schon da -> Zuordnen (Pax AI schlaegt vor,
  // ein Mensch bestaetigt); noch kein Framework -> Framework Assistant erzeugt L1-L4
  function startTransfer(survey) {
    if (survey.mode === "select") transferScope(survey);
    else if (framework.l2) mapping = mapping === survey.id ? null : survey.id;
    else transfer(survey);
  }

  function mappingSaved(survey, count) {
    mapping = null;
    notice = {
      ...notice,
      [survey.id]: `${count} ${count === 1 ? "task" : "tasks"} transferred to the framework.`,
    };
    dispatch("framework");
    load();
  }

  // Nur Umfrage 2: die gewaehlten Unteraufgaben werden der Collection scope in Abschnitt 2 (wie die Haekchen dort)
  async function transferScope(survey) {
    busy = survey.id;
    try {
      await loadResult(survey);
      const ids = results[survey.id].themes
        .flatMap((theme) => theme.tasks)
        .filter((task) => task.selected && task.subtask_id)
        .map((task) => task.subtask_id);
      const current = JSON.parse(
        localStorage.getItem(scopeKey(idproject)) || "[]",
      );
      if (
        current.length &&
        !(await askConfirm(
          "Replace collection scope?",
          `Replace the current collection scope (${current.length} subtasks) with the ${ids.length} subtasks the team chose?`,
          "Replace",
        ))
      )
        return;
      localStorage.setItem(scopeKey(idproject), JSON.stringify(ids));
      notice = {
        ...notice,
        [survey.id]: `${ids.length} subtasks set as collection scope. See section 2.`,
      };
      dispatch("scope");
    } catch (scopeError) {
      error = scopeError.message || "The browser storage is not available.";
    } finally {
      busy = "";
    }
  }

  async function startSelection(survey) {
    busy = survey.id;
    try {
      await request(`team-surveys/${survey.id}/selection`, { method: "POST" });
      await load();
    } catch (selectionError) {
      // Laeuft das Zusammenfassen schon im Hintergrund, ist das kein Fehler - die Liste zeigt es an
      if (selectionError.message === "the list is being prepared") await load();
      else error = selectionError.message;
    } finally {
      busy = "";
    }
  }

  async function update(survey, changes) {
    busy = survey.id;
    try {
      await request(`team-surveys/${survey.id}`, {
        method: "PATCH",
        body: JSON.stringify(changes),
      });
      await load();
      if (openResults[survey.id]) await loadResult(survey);
    } catch (updateError) {
      error = updateError.message;
    } finally {
      busy = "";
    }
  }

  // Im Assistenten liegt dieses Panel selbst in einem Modal; dessen Container hat ein transform, wodurch ein
  // verschachteltes Modal (position: fixed) darin abgeschnitten wird - daher den Loeschdialog an <body> haengen
  function portal(node) {
    document.body.appendChild(node);
    return { destroy: () => node.remove() };
  }

  // Rueckfragen als Modal statt window.confirm - askConfirm liefert true (bestaetigt) oder false (abgebrochen)
  let confirmDialog = null;
  function askConfirm(heading, text, confirmText, compact = false) {
    return new Promise(
      (resolve) =>
        (confirmDialog = { heading, text, confirmText, compact, resolve }),
    );
  }
  function closeConfirm(confirmed) {
    confirmDialog?.resolve(confirmed);
    confirmDialog = null;
  }

  function askDelete(survey) {
    deleting = survey;
    deleteConfirmName = "";
  }

  async function remove() {
    const survey = deleting;
    deleting = null;
    busy = survey.id;
    try {
      await request(`team-surveys/${survey.id}`, { method: "DELETE" });
      if (editing === survey.id) editing = null;
      await load();
    } catch (removeError) {
      error = removeError.message;
    } finally {
      busy = "";
    }
  }

  async function loadResult(survey) {
    results = {
      ...results,
      [survey.id]: await request(`team-surveys/${survey.id}`),
    };
  }

  async function toggleResult(survey) {
    openResults = { ...openResults, [survey.id]: !openResults[survey.id] };
    if (openResults[survey.id]) {
      try {
        await loadResult(survey);
      } catch (resultError) {
        error = resultError.message;
      }
    }
  }

  // Transfer: Der Assistent stellt seinen Entwurf beim Oeffnen aus diesem Speicher wieder her und analysiert
  // die Aufgabenliste (autoAnalyze) selbst - niemand muss sie eingeben. Mit genau einer Abteilung sofort
  // (Schritt 7), sonst nach der Abteilungswahl (Schritt 1).
  async function transfer(survey) {
    busy = survey.id;
    try {
      await loadResult(survey);
      const result = results[survey.id];
      if (
        localStorage.getItem(ASSISTANT_DRAFT_KEY) &&
        !(await askConfirm(
          "Replace draft?",
          "Replace the current Framework Assistant draft with this survey result?",
          "Replace",
        ))
      )
        return;
      const single = result.departments.length === 1;
      localStorage.setItem(
        ASSISTANT_DRAFT_KEY,
        JSON.stringify({
          step: single ? 7 : 1,
          generationMode: "bottom_up",
          started: true,
          selectedAreas: single ? result.departments : [],
          message: "",
          workDescriptionDraft: "",
          autoAnalyze: result.brief,
        }),
      );
      if (inAssistant) dispatch("assistantDraft");
      else navigate(ROUTES.frameworkAssistant);
    } catch (transferError) {
      error = transferError.message || "The browser storage is not available.";
    } finally {
      busy = "";
    }
  }

  function formatDate(value) {
    return new Date(value).toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function progress(survey) {
    // nur Sammeln: auch fertig zaehlen die Antworten, abgestimmt wird nicht
    const field =
      survey.stage === "collect" || survey.mode === "collect"
        ? "collect"
        : "select";
    const count = survey.invites.total
      ? survey.invites[`${field}_done`]
      : survey.answers[field];
    const expected = survey.expected_participants
      ? ` of ${survey.expected_participants}`
      : "";
    // Antworten ueber den allgemeinen Link zaehlen dazu, gehoeren aber zu keiner Einladung
    const viaLink = survey.invites.total ? survey.answers[field] - count : 0;
    return `${count}${expected} ${field === "collect" ? "answered" : "voted"}${viaLink > 0 ? ` (+${viaLink} via the shared link)` : ""}`;
  }

  onMount(load);
  onDestroy(() => clearTimeout(pollTimer));
</script>

<section class="team-survey-panel" class:in-assistant={inAssistant}>
  <!-- Im Assistenten keine Kopfzeile: sie enthielt dort nur "Set up team survey" - neue Umfragen laufen ueber
       "Ask your team" im Chat (vorher: {#if !inAssistant || !editing}) -->
  {#if !inAssistant}
    <header>
      <div>
        {#if !inAssistant}
          <h3>
            <button
              type="button"
              class="section-toggle"
              aria-expanded={open}
              on:click={() => (open = !open)}
            >
              <span class="section-step">1</span>
              Collect tasks with the team
              <span class="section-chevron" class:open aria-hidden="true"
              ></span>
            </button>
          </h3>
        {/if}
        {#if !inAssistant && open}
          <p>
            Everyone in the team lists their daily tasks via a link. The survey
            is anonymous and takes about 15 minutes. Pax AI merges identical
            tasks, and Transfer turns the result into the framework below.
          </p>
        {:else if !inAssistant && surveys.length}
          <p>{surveys.length} {surveys.length === 1 ? "survey" : "surveys"}</p>
        {/if}
      </div>
      {#if (open || inAssistant) && !editing}
        <Button
          text="Set up team survey"
          variant="secondary"
          size="sm"
          disabled={loading}
          on:click={() => openSettings(null)}
        />
      {/if}
    </header>
  {/if}

  {#if open || inAssistant}
    {#if error}<p class="panel-error">{error}</p>{/if}

    {#if editing}
      <div class="settings-form">
        <h4>{editing === "new" ? "New team survey" : "Survey settings"}</h4>
        {#if editing === "new"}
          <label>
            <span>Team</span>
            <select bind:value={settings.team_id} on:change={pickTeam}>
              <option value="" disabled>Select a team</option>
              {#each freeTeams as team}<option value={team.id}
                  >{team.name}{team.users
                    ? ` (${team.users} ${team.users === 1 ? "person" : "people"})`
                    : ""}</option
                >{/each}
              <option value="new">New team …</option>
            </select>
          </label>
          {#if settings.team_id === "new"}
            <label
              ><span>Team name</span><input
                bind:value={settings.team_name}
                maxlength="120"
                placeholder="e.g. Accounts Payable"
              /></label
            >
          {/if}
        {/if}
        <label class="wide">
          <span
            >Participants <em
              >one per line: Name &lt;e-mail&gt; or just the e-mail</em
            ></span
          >
          <textarea
            rows="5"
            bind:value={settings.participants}
            placeholder="Anna Beispiel <anna@example.com>"
          ></textarea>
          <em
            >Everyone gets a personal link; reminders only go to those who have
            not sent their answers yet. Leave empty to share one link yourself.</em
          >
          {#each participantErrors as problem}<em class="field-error"
              >{problem}</em
            >{/each}
        </label>
        <label>
          <span>Send invitations</span>
          {#if settings.start === "started"}
            <input value="Already sent" disabled />
          {:else}
            <select bind:value={settings.start}>
              <option value="now">Right away</option>
              <option value="later">At a later time</option>
              <option value="off">Not yet (no e-mails)</option>
            </select>
          {/if}
        </label>
        {#if settings.start === "later"}
          <label
            ><span>Invitations go out at</span><input
              type="datetime-local"
              bind:value={settings.start_at}
            /></label
          >
        {/if}
        {#if settings.mode !== "select"}<label
            ><span>Survey open until</span><input
              type="date"
              bind:value={settings.collect_until}
            /></label
          >{/if}
        {#if settings.mode !== "select"}
          <label
            ><span>Data collection from <em>optional</em></span><input
              type="date"
              bind:value={settings.period_from}
            /><em
              >e.g. 1 to 4 weeks after the survey; the survey asks per task
              whether it occurs then</em
            ></label
          >
          <label
            ><span>Data collection until</span><input
              type="date"
              bind:value={settings.period_until}
              min={settings.period_from || undefined}
            /></label
          >
        {/if}
        <div class="reminders wide">
          <span class="group-label"
            >Automatic reminders <em
              >to those who have not sent their answers yet</em
            ></span
          >
          <label class="check">
            <input type="checkbox" bind:checked={settings.reminder} /> First
            reminder
            <input
              type="number"
              min="1"
              max="30"
              bind:value={settings.reminder_days}
              disabled={!settings.reminder}
            /> days before the deadline
          </label>
          <label class="check">
            <input type="checkbox" bind:checked={settings.reminder2} /> Second
            reminder
            <input
              type="number"
              min="1"
              max="30"
              bind:value={settings.reminder2_days}
              disabled={!settings.reminder2}
            /> days before the deadline
          </label>
        </div>
        <label>
          <span>Language of the e-mails</span>
          <select bind:value={settings.language}>
            <option value="de">German</option><option value="en">English</option
            ><option value="fr">French</option>
            <option value="it">Italian</option><option value="es"
              >Spanish</option
            >
          </select>
          <em
            >{settings.language === "en"
              ? "English only."
              : "With the English version below as a fallback."} The survey itself
            opens in each person's system language.</em
          >
        </label>
        <label
          ><span>Contact person <em>optional</em></span><input
            bind:value={settings.contact_person}
            maxlength="300"
            placeholder="Name"
          /></label
        >
        <label
          ><span>Contact e-mail <em>optional</em></span><input
            type="email"
            bind:value={settings.contact_person_email}
            maxlength="300"
            placeholder="name@company.com"
          /></label
        >
        <label class="wide">
          <span>E-mail signature <em>optional</em></span>
          <textarea
            rows="2"
            bind:value={settings.email_signature}
            maxlength="1000"
            placeholder={settings.language === "de"
              ? "Euer Projektteam"
              : "Your project team"}
          ></textarea>
        </label>
        {#if editing !== "new"}
          {@const survey = surveys.find((item) => item.id === editing)}
          {#if survey}
            <div class="more-actions wide">
              <span class="group-label">More</span>
              <button
                type="button"
                class="link"
                on:click={() => copyLink(survey)}
                >{copied === survey.id ? "Link copied" : "Copy link"}</button
              >
              <button
                type="button"
                class="link"
                on:click={() => showMailPreview(survey)}
                >{mailPreview[survey.id]
                  ? "Hide e-mail"
                  : "E-mail preview"}</button
              >
              <button
                type="button"
                class="link"
                disabled={sendingTestMail}
                on:click={() => openTestMail(survey)}>Test mail</button
              >
              {#if survey.stage !== "done" && survey.invites.total && survey.start_at && new Date(survey.start_at) <= new Date()}
                <button
                  type="button"
                  class="link"
                  disabled={Boolean(busy)}
                  on:click={() => remindNow(survey)}>Remind now</button
                >
              {/if}
              {#if survey.stage === "select" && survey.mode === "both" && !survey.answers.select}
                <button
                  type="button"
                  class="link"
                  on:click={() => startSelection(survey)}>Merge again</button
                >
              {/if}
              {#if survey.stage === "done" && survey.mode !== "collect"}
                <button
                  type="button"
                  class="link"
                  on:click={() => update(survey, { stage: "select" })}
                  >Reopen choosing</button
                >
              {/if}
            </div>
            {#if mailPreview[survey.id]}
              <div class="mail-preview wide">
                <strong>{mailPreview[survey.id].subject}</strong>
                <pre>{mailPreview[survey.id].text}</pre>
              </div>
            {/if}
          {/if}
        {/if}
        <div class="create-actions">
          <em>Reminders go out at 9:00 on the chosen day.</em>
          <Button
            text="Cancel"
            variant={inAssistant ? "secondary" : "outline"}
            size="sm"
            on:click={() => (editing = null)}
          />
          <Button
            text={busy === "settings"
              ? "Saving …"
              : editing === "new"
                ? "Create survey"
                : "Save settings"}
            variant={inAssistant ? "secondary" : "primary"}
            size="sm"
            disabled={busy === "settings" ||
              (editing === "new" &&
                (!settings.team_id ||
                  (settings.team_id === "new" && !settings.team_name.trim())))}
            on:click={saveSettings}
          />
        </div>
      </div>
    {/if}

    {#each surveys as survey (survey.id)}
      {@const step = nextStep(survey)}
      <article class="survey-row">
        <div class="survey-main">
          <strong>{survey.team}</strong>
          <span class="stage {survey.stage}">{STAGES[survey.stage]}</span>
          <span class="muted">
            {progress(survey)}
            {#if survey.overdue}
              · deadline passed, still collecting until you close the survey
            {:else if survey.stage !== "done"}
              · until {formatDate(
                survey.stage === "collect"
                  ? survey.collect_until
                  : survey.select_until,
              )}{/if}
            {#if survey.tasks}
              · {survey.tasks}
              {survey.mode === "select"
                ? "tasks from the framework"
                : "merged tasks"}{/if}
          </span>
        </div>
        <p class="muted mail-line">{mailLine(survey)}</p>
        {#if notice[survey.id]}<p class="row-notice">
            {notice[survey.id]}
          </p>{/if}
        {#if survey.method === "names"}
          <p class="method-hint">
            Pax AI was not reachable, so only identical names were merged.
          </p>
        {/if}
        <div class="survey-actions">
          {#if survey.preparing || (busy === survey.id && survey.stage === "collect")}
            <span class="muted"
              >Pax AI is merging the answers. This takes up to a minute …</span
            >
          {:else if busy === survey.id}
            <span class="muted">Saving …</span>
          {:else}
            <!-- Normale Buttons statt Textlinks; title am Wrapper, weil Button selbst keinen title durchreicht.
               Reihenfolge wie der Ablauf: naechster Schritt, Ergebnis, Transfer - dann Vorschau, Einstellungen, Loeschen -->
            {#if step}
              <span title={step.title}
                ><Button
                  text={step.label}
                  variant="secondary"
                  size="sm"
                  notAllowed={step.disabled}
                  on:click={step.run}
                /></span
              >
            {/if}
            <span
              title={survey.stage === "collect"
                ? "Available once the answers are merged"
                : ""}
            >
              <Button
                text={openResults[survey.id] ? "Hide result" : "Show result"}
                variant="secondary"
                size="sm"
                notAllowed={survey.stage === "collect"}
                on:click={() => toggleResult(survey)}
              />
            </span>
            {#if assistantEnabled && isAdmin}
              <span
                title={survey.stage !== "done"
                  ? survey.mode === "collect"
                    ? "Close the survey first"
                    : "Close survey 2 first"
                  : survey.mode === "select"
                    ? "Sets the chosen subtasks as collection scope in section 2"
                    : framework.l2
                      ? "Assigns the tasks to your existing processes. Pax AI suggests and you confirm"
                      : "Creates the L1 to L4 framework with the Framework Assistant"}
              >
                <Button
                  text="Transfer"
                  variant="secondary"
                  size="sm"
                  notAllowed={survey.stage !== "done"}
                  on:click={() => startTransfer(survey)}
                />
              </span>
            {/if}
            <span title="Nothing is saved in the preview">
              <Button
                text="Preview"
                variant="secondary"
                size="sm"
                on:click={() =>
                  window.open(
                    `${surveyLink(survey)}&preview=1`,
                    "_blank",
                    "noreferrer",
                  )}
              />
            </span>
            <Button
              text="Settings"
              variant="secondary"
              size="sm"
              on:click={() => openSettings(survey)}
            />
            <Button
              text="Delete"
              variant="secondary"
              size="sm"
              on:click={() => askDelete(survey)}
            />
          {/if}
        </div>

        {#if mapping === survey.id}
          <TeamSurveyMapping
            surveyId={survey.id}
            on:close={() => (mapping = null)}
            on:saved={(event) => mappingSaved(survey, event.detail.count)}
          />
        {/if}

        {#if openResults[survey.id] && results[survey.id]}
          {@const result = results[survey.id]}
          <div class="result">
            {#each result.themes as theme}
              <h4>{theme.name}</h4>
              <table>
                <tbody>
                  {#each theme.tasks as task}
                    <tr class:selected={task.selected}>
                      <!-- Auswahl und Stimmen nur mit Abstimmung - nur Sammeln zaehlt jede Aufgabe -->
                      {#if survey.mode !== "collect"}<td class="mark"
                          >{task.selected ? "✓" : ""}</td
                        >{/if}
                      <td>
                        <strong>{task.name}</strong>
                        {#if task.names.length > 1}<span class="muted">
                            · named as {task.names.join(", ")}</span
                          >{/if}
                        {#each task.comments as comment}<p class="comment">
                            “{comment}”
                          </p>{/each}
                      </td>
                      <td class="muted facts_cell">
                        {#if task.mentions}<span>{task.mentions}× named</span
                          >{/if}
                        {#if task.in_period?.yes || task.in_period?.no}<span
                            >In the period: {task.in_period.yes} yes · {task
                              .in_period.no} no</span
                          >{/if}
                      </td>
                      {#if survey.mode !== "collect"}<td
                          class="muted votes_cell"
                          >{task.yes} yes · {task.no} no</td
                        >{/if}
                    </tr>
                  {/each}
                </tbody>
              </table>
            {/each}
            {#if result.missing.length}
              <h4>Named as missing</h4>
              <ul>
                {#each result.missing as item}
                  <li>
                    <strong>{item.name}</strong>{item.description
                      ? `: ${item.description}`
                      : ""}
                    <span class="muted">
                      · {[
                        item.frequency,
                        item.digital,
                        item.continuity,
                        item.standardization,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "no details"}{item.share
                        ? ` · ${item.share} %`
                        : ""}</span
                    >
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
        {/if}
      </article>
    {/each}
  {/if}
</section>

<div use:portal>
  <Modal
    modalClass="team-survey-dialog test-mail-dialog"
    buttonSize={inAssistant ? "sm" : "md"}
    open={Boolean(testMailSurvey)}
    autowidth
    preventCloseOnClickOutside
    modalHeading="Send test invitation"
    subtitle="The invitation link opens the preview. Nothing is saved."
    primaryButtonText={sendingTestMail ? "Sending…" : "Send test mail"}
    primaryButtonVariant="secondary"
    noSecondaryButton
    actions
    primaryButtonAction={sendTestMail}
    primaryButtonValidations={sendingTestMail ||
      !testEmail.trim() ||
      testEmailInvalid}
    on:close={() => (testMailSurvey = null)}
  >
    <TextInput
      id="team-survey-test-email"
      type="email"
      labelText="E-mail address"
      placeholder="name@company.com"
      required
      disabled={sendingTestMail}
      bind:value={testEmail}
      bind:ref={testEmailInput}
      on:input={(event) => validateTestEmail(event.target)}
    />
    {#if testEmailInvalid}<p class="error" role="alert">
        Enter a valid e-mail address.
      </p>{/if}
    {#if testMailError}<p class="error" role="alert">{testMailError}</p>{/if}
    <Button
      slot="left-buttons"
      text="Cancel"
      variant="secondary"
      size={inAssistant ? "sm" : "md"}
      on:click={() => (testMailSurvey = null)}
    />
  </Modal>
</div>

<div use:portal>
  <Modal
    modalClass="team-survey-dialog"
    buttonSize={inAssistant ? "sm" : "md"}
    open={Boolean(deleting)}
    preventCloseOnClickOutside
    modalHeading="Delete Survey?"
    primaryButtonText="Delete"
    secondaryButtonText="Cancel"
    actions
    width="undefined"
    primaryButtonAction={remove}
    primaryButtonValidations={deleteNameMismatch}
    on:click:button--secondary={() => (deleting = null)}
    on:close={() => (deleting = null)}
  >
    <p class="deleteModalBody">
      Are you sure you want to delete the team survey: <span class="survey-name"
        >{deleting?.team}</span
      >? All answers of the team are deleted with it.
      <span>This action cannot be undone.</span>
    </p>
    <TextInput
      labelText={`Type "${deleting?.team || ""}" to confirm`}
      placeholder="Enter the exact name"
      bind:value={deleteConfirmName}
      invalid={deleteConfirmName.length > 0 && deleteNameMismatch}
      invalidText="The name does not match."
    />
  </Modal>
</div>

<div use:portal>
  <Modal
    modalClass={`team-survey-dialog ${confirmDialog?.compact ? "test-mail-dialog" : ""}`}
    buttonSize={inAssistant ? "sm" : "md"}
    open={Boolean(confirmDialog)}
    autowidth={Boolean(confirmDialog?.compact)}
    preventCloseOnClickOutside
    modalHeading={confirmDialog?.heading || ""}
    primaryButtonText={confirmDialog?.confirmText || "Confirm"}
    primaryButtonVariant="secondary"
    noSecondaryButton
    actions
    primaryButtonAction={() => closeConfirm(true)}
    on:close={() => closeConfirm(false)}
  >
    <p
      class="deleteModalBody"
      class:compact-confirmation={confirmDialog?.compact}
    >
      {confirmDialog?.text || ""}
    </p>
    <Button
      slot="left-buttons"
      text="Cancel"
      variant="secondary"
      size={inAssistant ? "sm" : "md"}
      on:click={() => closeConfirm(false)}
    />
  </Modal>
</div>

<style>
  .team-survey-panel {
    display: grid;
    gap: 0.75rem;
    padding: 1rem;
    border: 1px solid var(--pax-smoke);
    border-radius: 0.5rem;
    background: var(--pax-white);
    color: var(--pax-black);
  }
  .in-assistant {
    gap: var(--control-gap);
    padding: 0;
    border: 0;
  }
  .in-assistant header {
    gap: var(--control-gap);
  }
  .in-assistant header p {
    padding-left: 0;
  }
  .in-assistant .settings-form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--control-gap);
  }
  .in-assistant .settings-form > label {
    min-width: 0;
  }
  .in-assistant label {
    gap: var(--control-gap);
  }
  .in-assistant .reminders {
    gap: var(--control-gap);
  }
  .in-assistant .check {
    flex-wrap: wrap;
  }
  .in-assistant .create-actions {
    position: sticky;
    bottom: -1rem;
    z-index: 1;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: var(--control-gap);
    margin: 0 -1rem -1rem;
    padding: 1rem;
    border-top: 1px solid var(--border-color);
    background: var(--pax-white);
  }
  .in-assistant .create-actions em {
    flex-basis: 100%;
  }
  @media (max-width: 40rem) {
    .in-assistant .settings-form {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }
  header > div {
    flex: 1 1 24rem;
    min-width: 0;
  }
  h3 {
    margin: 0 0 0.25rem;
    font-size: 1rem;
    font-weight: 600;
  }
  /* Abschnittskopf wie "2 Framework" in ProcessPresets */
  .section-toggle {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }
  .section-step {
    display: grid;
    place-items: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background: var(--pax-black);
    color: var(--pax-white);
    font-size: 0.75rem;
  }
  .section-chevron {
    width: 0.5rem;
    height: 0.5rem;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(-45deg);
    transition: transform 0.2s ease;
  }
  .section-chevron.open {
    transform: rotate(45deg);
  }
  header p {
    padding-left: 2.1rem;
  }
  .divider {
    color: #cfd4da;
    font-size: 0.8rem;
  }
  .more-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem 1rem;
    padding-top: 0.5rem;
    border-top: 1px solid #dfe3e8;
  }
  .more-actions .group-label {
    font-size: 0.8rem;
    font-weight: 600;
  }
  header p {
    max-width: 52rem;
    margin: 0;
    color: #59616c;
    font-size: 0.875rem;
    line-height: 1.45;
  }
  .panel-error {
    margin: 0;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    background: var(--pax-red);
    font-size: 0.875rem;
  }
  .settings-form {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
    align-items: start;
    gap: 0.75rem 1rem;
    padding: 1rem;
    border-radius: 0.5rem;
    background: var(--pax-smoke);
  }
  .settings-form h4 {
    grid-column: 1 / -1;
    margin: 0;
  }
  .settings-form .wide {
    grid-column: 1 / -1;
  }
  label em,
  .settings-form em {
    color: #59616c;
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 400;
  }
  .settings-form .field-error {
    color: #b42318;
  }
  textarea {
    box-sizing: border-box;
    width: 100%;
    padding: 0.5rem 0.6rem;
    border: 1px solid #cfd4da;
    border-radius: 0.4rem;
    background: var(--pax-white);
    font: inherit;
    font-weight: 400;
    resize: vertical;
  }
  .reminders {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 2rem;
  }
  .reminders .group-label {
    flex: 1 0 100%;
    font-size: 0.8rem;
    font-weight: 600;
  }
  .check {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 400;
  }
  .check input[type="checkbox"] {
    width: auto;
    height: auto;
  }
  .check input[type="number"] {
    width: 4rem;
  }
  .mail-line {
    margin: 0;
  }
  .row-notice {
    margin: 0;
    padding: 0.4rem 0.6rem;
    border-radius: 0.4rem;
    background: var(--pax-green);
    font-size: 0.8rem;
  }
  .mail-preview {
    padding: 0.75rem 1rem;
    border: 1px dashed #cfd4da;
    border-radius: 0.5rem;
    background: #fafafa;
    font-size: 0.8rem;
  }
  .mail-preview pre {
    margin: 0.5rem 0 0;
    font-family: inherit;
    white-space: pre-wrap;
  }
  label {
    display: grid;
    gap: 0.3rem;
    font-size: 0.8rem;
    font-weight: 600;
  }
  input,
  select {
    box-sizing: border-box;
    width: 100%;
    height: 2.5rem;
    padding: 0 0.6rem;
    border: 1px solid #cfd4da;
    border-radius: 0.4rem;
    background: var(--pax-white);
    font: inherit;
    font-weight: 400;
  }
  .create-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    grid-column: 1 / -1;
  }
  .create-actions em {
    margin-right: auto;
    color: #59616c;
    font-size: 0.75rem;
    font-style: normal;
  }
  .survey-row {
    display: grid;
    gap: 0.5rem;
    padding: 0.75rem 0 0;
    border-top: 1px solid var(--pax-smoke);
  }
  /* Im Assistenten ohne Kopfzeile: keine zweite Linie direkt unter der Modal-Trennlinie */
  /* Gesperrte Buttons empfangen kein Hover - der Tooltip (title) am umschliessenden span soll trotzdem erscheinen */
  .survey-actions span[title] :global(button:disabled) {
    pointer-events: none;
  }
  .in-assistant .survey-row:first-of-type {
    padding-top: 0;
    border-top: 0;
  }
  /* Dialoge ueber dem Umfrage-Fenster des Assistenten (z-index 100000 in FrameworkAssistant.svelte) */
  :global(.custom-modal.team-survey-dialog) {
    z-index: 100001;
  }
  :global(.custom-modal.test-mail-dialog .bx--modal-container) {
    width: calc(100% - 2rem);
    max-width: 34rem;
  }
  :global(.custom-modal.test-mail-dialog .bx--modal-header) {
    padding-top: 0.5rem;
    margin-bottom: 0.75rem;
  }
  :global(.custom-modal.test-mail-dialog .divider) {
    margin-bottom: 0.5rem;
  }
  :global(.custom-modal.test-mail-dialog .text-input) {
    margin-top: 0.5rem;
  }
  :global(.custom-modal.test-mail-dialog .divider-margin) {
    margin-top: 0.75rem;
  }
  :global(.custom-modal.test-mail-dialog .buttons-container) {
    margin-top: 0.75rem;
    padding-bottom: 0.25rem !important;
  }
  .survey-main {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }
  .stage {
    padding: 0.15rem 0.6rem;
    border-radius: 1rem;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .stage.collect {
    background: var(--pax-blue);
  }
  .stage.select {
    background: var(--pax-yellow);
  }
  .stage.done {
    background: var(--pax-green);
  }
  .muted {
    color: #59616c;
    font-size: 0.8rem;
  }
  .method-hint {
    margin: 0;
    padding: 0.4rem 0.6rem;
    border-radius: 0.4rem;
    background: var(--pax-yellow);
    font-size: 0.8rem;
  }
  .survey-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--control-gap);
  }
  .link {
    padding: 0;
    border: 0;
    background: none;
    color: var(--pax-black);
    font: inherit;
    font-size: 0.8rem;
    text-decoration: underline;
    cursor: pointer;
  }
  .link:disabled {
    color: #98a2b3;
    cursor: default;
  }
  .link.strong {
    font-weight: 600;
  }
  .link.danger {
    color: #b42318;
  }
  .result {
    display: grid;
    gap: 0.25rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    background: #fafafa;
  }
  h4 {
    margin: 0.5rem 0 0;
    font-size: 0.85rem;
    font-weight: 600;
  }
  h4:first-child {
    margin-top: 0;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 0.8rem;
  }
  .facts_cell {
    width: 12.5rem;
  } /* "In the period: 12 yes · 12 no" bricht nicht um */
  .votes_cell {
    width: 6.5rem;
  }
  td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--pax-smoke);
    vertical-align: middle;
  }
  tr:last-child td {
    border-bottom: none;
  }
  tr:not(.selected) td {
    color: #7a8491;
  }
  .mark {
    width: 1.5rem;
    color: #1e7b34;
    font-weight: 700;
  }
  .comment {
    margin: 0.2rem 0 0;
    color: #59616c;
    font-style: italic;
  }
  ul {
    margin: 0;
    padding-left: 1.2rem;
    font-size: 0.8rem;
  }
  .deleteModalBody {
    padding: 23px 0;
    font-family: Inter;
    font-size: 14px;
  }
  .deleteModalBody.compact-confirmation {
    margin: 0;
    padding: 0.5rem 0;
  }
  .deleteModalBody span {
    font-weight: 700;
  }
  .deleteModalBody :last-child {
    color: var(--pax-orange);
  }
  .facts_cell span {
    display: block;
    white-space: nowrap;
  }
</style>
